import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  Index,
  IndexCreatedEnvelope,
  IndexDeletedEnvelope,
  IndexUpdatedEnvelope,
  ListAvailableIndexesResponse,
  ListIndexesOptions,
  ListIndexesResponse,
  createIndex as universalCreateIndex,
  dropIndex as universalDropIndex,
  listAvailableIndexes as universalListAvailableIndexes,
  listIndexes as universalListIndexes,
} from './data-v2-index-indexes.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/data' };

export function createIndex(httpClient: HttpClient): CreateIndexSignature {
  return (
    dataCollectionId: string,
    index: NonNullablePaths<
      Index,
      `fields` | `fields.${number}.path` | `name`,
      4
    >
  ) =>
    universalCreateIndex(
      dataCollectionId,
      index,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateIndexSignature {
  /**
   * Creates an index for a data collection.
   *
   * The index can't be used immediately, as the process of generating the index takes time.
   * You can check whether an index is ready by calling List Indexes.
   *
   * Note that when an index fails to create, the failed index still occupies a slot.
   * To remove the failed index and free up the slot for a new index, call Drop Index.
   * @param - ID of the data collection for which to generate the index.
   * @param - Details of the index to be created.
   * @param - Options for creating an index.
   * @returns Details of the index being generated.
   */
  (
    dataCollectionId: string,
    index: NonNullablePaths<
      Index,
      `fields` | `fields.${number}.path` | `name`,
      4
    >
  ): Promise<
    NonNullablePaths<
      Index,
      | `_id`
      | `name`
      | `fields`
      | `fields.${number}.path`
      | `fields.${number}.order`
      | `status`
      | `failure.code`
      | `failure.description`
      | `unique`
      | `caseInsensitive`
      | `source`,
      4
    >
  >;
}

export function dropIndex(httpClient: HttpClient): DropIndexSignature {
  return (dataCollectionId: string, indexName: string) =>
    universalDropIndex(
      dataCollectionId,
      indexName,
      // @ts-ignore
      { httpClient }
    );
}

interface DropIndexSignature {
  /**
   * Removes an index from a data collection.
   *
   * The process of dropping an index from a collection takes time.
   * You can check whether an index has been dropped by calling List Indexes.
   * @param - ID of the data collection for which the index to be dropped is defined.
   * @param - Name of the index to drop.
   * @param - Options for dropping an index.
   */
  (dataCollectionId: string, indexName: string): Promise<void>;
}

export function listIndexes(httpClient: HttpClient): ListIndexesSignature {
  return (dataCollectionId: string, options?: ListIndexesOptions) =>
    universalListIndexes(
      dataCollectionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListIndexesSignature {
  /**
   * Lists all indexes defined for a data collection.
   *
   * When an index's status is `ACTIVE`, it is ready to use.
   * While it is still being created, its status is `BUILDING`.
   *
   * When an index's status is `DROPPED`, it has been dropped successfully.
   * While it is still in the process of being removed, its status is `DROPPING`.
   * @param - ID of the data collection for which to list indexes.
   * @param - Options for retrieving a list of indexes.
   */
  (dataCollectionId: string, options?: ListIndexesOptions): Promise<
    NonNullablePaths<
      ListIndexesResponse,
      | `indexes`
      | `indexes.${number}._id`
      | `indexes.${number}.name`
      | `indexes.${number}.status`
      | `indexes.${number}.failure.code`
      | `indexes.${number}.failure.description`
      | `indexes.${number}.unique`
      | `indexes.${number}.caseInsensitive`
      | `indexes.${number}.source`,
      5
    >
  >;
}

/** @internal */
export function listAvailableIndexes(
  httpClient: HttpClient
): ListAvailableIndexesSignature {
  return (dataCollectionId: string) =>
    universalListAvailableIndexes(
      dataCollectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAvailableIndexesSignature {
  /**
   * Lists available indexes limits
   * @param - Data collection to show available indexes for
   */
  (dataCollectionId: string): Promise<
    NonNullablePaths<
      ListAvailableIndexesResponse,
      `regular1Field` | `regular3Field` | `unique1Field`,
      2
    >
  >;
}

export const onIndexCreated = EventDefinition(
  'wix.data.v2.index_created',
  true,
  (event: IndexCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<IndexCreatedEnvelope>();
export const onIndexDeleted = EventDefinition(
  'wix.data.v2.index_deleted',
  true,
  (event: IndexDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<IndexDeletedEnvelope>();
export const onIndexUpdated = EventDefinition(
  'wix.data.v2.index_updated',
  true,
  (event: IndexUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<IndexUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  CreateIndexRequest,
  CreateIndexResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  DropIndexRequest,
  DropIndexResponse,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Environment,
  EventMetadata,
  Failure,
  Field,
  IdentificationData,
  IdentificationDataIdOneOf,
  Index,
  IndexCreatedEnvelope,
  IndexDeletedEnvelope,
  IndexSource,
  IndexUpdatedEnvelope,
  ListAvailableIndexesRequest,
  ListAvailableIndexesResponse,
  ListIndexesOptions,
  ListIndexesRequest,
  ListIndexesResponse,
  MessageEnvelope,
  Order,
  Paging,
  PagingMetadata,
  RestoreInfo,
  Status,
  WebhookIdentityType,
} from './data-v2-index-indexes.universal.js';
